/**
 * A group's consumption queue as rows, in the order the planner walks it:
 * a name filter past 8 lists, the first 8 then "Show all", each row's score
 * or why it is not ranked (FORMULA), and — MANUAL only, never filtered (a
 * subset cannot be reordered honestly) — drag to reorder. Shown under the
 * group row and, live, in the list formula pop-up — there every list, the
 * rows filling the panel to its bottom edge and scrolling under the filter.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DropGenListRankingPreview } from '@trafficpulse/shared';

import {
  dragEndToMove,
  useBinomDragSensors,
} from '@/features/flow-optimizer/tabs/standard/binom/workspace/list/use-binom-drag-sensors';

import { TagColorsProvider } from '../../rules/TagChips';
import { formulaNote } from '../formula/queue-order';
import { OrderQueueRow } from '../OrderQueueRow';

const ROW_CAP = 8;

export interface QueueRow {
  wiserId: number;
  name: string | null;
  leadCount: number | null;
}

interface Props {
  ordered: QueueRow[];
  preview: DropGenListRankingPreview | undefined;
  /** MANUAL order — rows drag (while not filtered). */
  manual: boolean;
  onReorder?: (wiserIds: number[]) => void;
  /** Every list, filling its (flex) parent and scrolling — the pop-up's panel. */
  fill?: boolean;
}

export const QueueList: FC<Props> = ({ ordered, preview, manual, onReorder, fill }) => {
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const sensors = useBinomDragSensors();
  const needle = filter.trim().toLowerCase();
  const matches = needle ? ordered.filter((r) => (r.name ?? '').toLowerCase().includes(needle)) : ordered;
  const rendered = needle || showAll || fill ? matches : matches.slice(0, ROW_CAP);
  const canDrag = manual && needle === '';
  const tagsOf = new Map((preview?.lists ?? []).map((l) => [l.wiserId, l.tags]));
  const handleDragEnd = (event: DragEndEvent) => {
    const move = dragEndToMove(event, ordered.map((r) => String(r.wiserId)));
    if (move && onReorder) onReorder(arrayMove(ordered.map((r) => r.wiserId), move.from, move.to));
  };
  return (
    <>
      {manual && needle ? <p className="text-[11px] text-muted-foreground">Clear the filter to reorder — a subset cannot be dragged honestly.</p> : null}
      {ordered.length > ROW_CAP ? (
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${ordered.length} lists by name…`}
          className="h-7 rounded-md border border-border/60 bg-background px-2 text-xs outline-none focus:border-primary"
        />
      ) : null}
      <TagColorsProvider colors={preview?.tagColors}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rendered.map((r) => String(r.wiserId))} strategy={verticalListSortingStrategy}>
          <div className={fill ? 'flex min-h-0 flex-1 flex-col overflow-y-auto pb-3 [scrollbar-gutter:stable]' : 'flex max-h-72 flex-col overflow-y-auto'}>
            {rendered.map((row) => (
              <OrderQueueRow
                key={row.wiserId}
                wiserId={row.wiserId}
                rank={ordered.indexOf(row) + 1}
                name={row.name ?? `#${row.wiserId}`}
                leadCount={row.leadCount}
                draggable={canDrag}
                note={preview ? formulaNote(row.wiserId, preview) : undefined}
                tags={tagsOf.get(row.wiserId)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      </TagColorsProvider>
      {!fill && !needle && matches.length > ROW_CAP ? (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="self-start text-[11px] font-medium text-primary hover:underline">
          {showAll ? 'Show fewer' : `Show all ${matches.length} lists`}
        </button>
      ) : null}
    </>
  );
};
